import React, { useEffect, useState, useCallback, useRef } from "react";
import { normalizeEmployeeName } from "@/modules/reservations/utilities/strings/normalizeNames";
import {
  AddIcon,
  CalendarIcon,
  CommandIcon,
  DeleteIcon,
  EmployeeServiceIcon,
  ValidatingIcon,
} from "@Models/icons";
import style from "./AppointmentDetail.module.css";
import {
  TertiaryButton,
  SelectInput,
  Icon,
  IconWrapper,
  TextAreaInput,
  //@ts-ignore
} from "@viuti/recursos";
import SearchSelect from "@Molecules/SearchSelect/SearchSelect";
import { getAvailableHours_adapter } from "@Adapters/employee/getAvailableHours.adapter";
import { HiOutlineBarsArrowDown } from "react-icons/hi2";
import { Summary } from "./Summary/Summary";
import { ServicePriceDuration } from "./ServicePriceDuration/ServicePriceDuration";
import { ConflictMessage } from "./ConflictMessage/ConflictMessage";
import { ValidationMessage } from "./ValidationMessage/ValidationMessage";
import { OccupiedEmployeesMessage } from "./OccupiedEmployeesMessage/OccupiedEmployeesMessage";
import { useValidateScheduleConflicts } from "./hook/useValidateScheduleConflicts";
import { AppointmentRow, ServiceOption } from "./interfaces/appointmentRow";
import { formatToSoles } from "@Utilities/FormatsHandler";
import { hasModuleAccess } from "@/utils/shared/permissions/hasModuleAccess";

const EDIT_DURATION_UUID = "4275773e-62d3-11f1-a596-8706899d777b";
const EDIT_SCHEDULES_UUID = "4276bee6-62d3-11f1-a596-8706899d777b";
const MANAGE_SERVICE_EMPLOYEES_UUID = "4276c940-62d3-11f1-a596-8706899d777b";
const UNLOCK_FIXED_RESERVATION_UUID = "e1f2a3b4-5c6d-7e8f-9a0b-1c2d3e4f5a6b";

interface AppointmentDetailProps {
  selectedItems: AppointmentRow[];
  generateId: () => string;
  services: any;
  setSelectedItems: any;
  date: string;
  setStateResponse: any;
  additionalComments: string;
  setAdditionalComments: any;
  isEdit: boolean;
  availableHours: any;
  setAvailableHours: any;
  loadingServices: boolean;
  setMessageConflicting: any;
  messageConflicting: string;
  empleadosOcupados: any;
  setEmpleadosOcupados: any;
  idConflictingItem: any;
  setIdConflictingItem: any;
  incompleteItems: any;
  setIncompleteItems: any;
  orderHours: () => void;
  reserveId: string;
  isDisabled: boolean;
  isDisabledNoShow: boolean;
  loadingHours: boolean;
  setLoadingHours: any;
  loadingHoursId: string;
  setLoadingHoursId: any;
  getStatusMessage: () => string;
  customerSelected: boolean;
  onOpenSchedulesModal?: (employeeId: number) => void;
  onOpenServicesModal?: (employeeId: number, serviceId: number) => void;
  preferredHour?: string;
  preferredEmployeeName?: string;
  highlightDetailId?: number;
}

export const AppointmentDetail = ({
  selectedItems,
  generateId,
  services,
  setSelectedItems,
  date,
  setStateResponse,
  additionalComments,
  setAdditionalComments,
  isEdit,
  availableHours,
  setAvailableHours,
  loadingServices,
  setMessageConflicting,
  messageConflicting,
  empleadosOcupados,
  setEmpleadosOcupados,
  idConflictingItem,
  setIdConflictingItem,
  incompleteItems,
  setIncompleteItems,
  orderHours,
  reserveId,
  isDisabled,
  isDisabledNoShow,
  loadingHours,
  setLoadingHours,
  loadingHoursId,
  setLoadingHoursId,
  getStatusMessage,
  customerSelected,
  onOpenSchedulesModal,
  onOpenServicesModal,
  preferredHour,
  preferredEmployeeName,
  highlightDetailId,
}: AppointmentDetailProps) => {
  const [errorMessage, setErrorMessage] = useState({});

  const [animationClass, setAnimationClass] = useState([]);

  // Preferencias de creación desde un cuadrante del calendario (clic en agenda).
  // Se consumen una sola vez: la hora al cargar los horarios del empleado y el
  // empleado al elegir el primer servicio.
  const preferredHourRef = useRef<string | undefined>(preferredHour);
  const preferredEmployeeRef = useRef<string | undefined>(preferredEmployeeName);

  // Selecciona la hora disponible cuyo bloque contiene el instante del clic.
  // Si el cuadrante no cae dentro de ningún horario disponible, devuelve null
  // (la hora queda vacía y el usuario la elige manualmente).
  const pickHourForSlot = (
    hoursList: string[],
    target: string
  ): string | null => {
    if (!hoursList || hoursList.length === 0 || !target) return null;
    const toMin = (s: string) => {
      const [h, m] = String(s).split(":").map(Number);
      return h * 60 + (m || 0);
    };
    const t = toMin(target);
    const sorted = [...hoursList].sort((a, b) => toMin(a) - toMin(b));
    const exact = sorted.find((s) => toMin(s) === t);
    if (exact) return exact;
    // Granularidad = menor diferencia entre horarios consecutivos.
    let gran = 30;
    if (sorted.length > 1) {
      const diffs = sorted
        .slice(1)
        .map((s, i) => toMin(s) - toMin(sorted[i]))
        .filter((d) => d > 0);
      if (diffs.length) gran = Math.min(...diffs);
    }
    const covering = sorted.find((s) => t >= toMin(s) && t < toMin(s) + gran);
    return covering || null;
  };

  useEffect(() => {
    if (!isEdit) {
      setSelectedItems((prevItems) =>
        prevItems.map((item) => ({ ...item, employee: "", schedule: "" }))
      );
      setAvailableHours({});
      setErrorMessage({});
    }
  }, [date]);

  const serviceItems = services.map((service) => ({
    name: service.namePrice,
    value: service.idPrice,
    price: service.price,
    duration: service.duration,
  }));

  const serviceOptions: ServiceOption[] = serviceItems.map((service) => ({
    id: service.value,
    label: service.name,
    subtitle: formatToSoles(service.price),
    // duration: service.duration,
  }));

  const handleAddService = () => {
    // Validar que todos los campos requeridos estén completos.
    // El estilista puede quedar "Por asignar" (value ""), por lo que NO se exige.
    const allFieldsCompleted = selectedItems.every(
      (item) => item.service && item.schedule
    );

    if (!allFieldsCompleted) {
      // Si falta completar algún campo, no se añade un nuevo servicio
      setMessageConflicting(
        "Por favor, completa todos los campos antes de añadir otro servicio."
      );
      const incompleteItems = selectedItems
        .filter((item) => !item.service || item.schedule === "")
        .map((item) => item.id);
      setIncompleteItems(incompleteItems);
      return; // Salir de la función
    }
    const newService = { id: generateId(), service: "", employee: "" };
    setSelectedItems((prevItems) => [...prevItems, newService]);
    // Agregar la clase de animación
    setAnimationClass((prev) => [...prev, newService.id]);
  };

  useEffect(() => {
    // "Por asignar" (employee "") es válido: solo se exige servicio y hora.
    const allFieldsCompleted = selectedItems.every(
      (item) => item.service && item.schedule
    );
    if (allFieldsCompleted) {
      setIncompleteItems([]);
    }
  }, [selectedItems]);

  const handleRemoveService = (id) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== id));
  };

  const handleSearchServiceChange = (id, value) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === id ? { ...item, serviceSearch: value } : item
      )
    );
  };

  // Función memoizada para filtrar servicios
  const getFilteredServiceOptions = useCallback(
    (searchTerm: string) => {
      if (!searchTerm) return serviceOptions;

      const searchLower = searchTerm.toLowerCase();
      return serviceOptions.filter(
        (option) =>
          option.label.toLowerCase().includes(searchLower) ||
          option.subtitle.toLowerCase().includes(searchLower)
      );
    },
    [serviceOptions]
  );

  const formatSelectedOption = (serviceId: number) => {
    const service = services.find((service) => service.idPrice === serviceId);

    if (!service) return null;

    return {
      id: service.idPrice,
      label: service.namePrice,
      // subtitle: `${formatToSoles(service.price)}`,
    };
  };

  const handleServiceChange = async (appointmentId: string, service) => {
    const serviceId = service ? service.id : service;
    const svc = services?.find((s) => s.idPrice === serviceId);
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === appointmentId
          ? {
              ...item,
              service: serviceId,
              employee: "",
              schedule: "",
              duration: svc?.duration || 0,
            }
          : item
      )
    );
    setErrorMessage((prev) => ({
      ...prev,
      [appointmentId]: "",
    }));

    // Preselección de empleado al crear desde un cuadrante de la vista diaria:
    // si el empleado de la columna ofrece este servicio, se asigna solo y se
    // cargan sus horarios (encadenando la posible preselección de hora).
    if (!isEdit && preferredEmployeeRef.current && svc?.employees?.length) {
      const targetName = normalizeEmployeeName(
        preferredEmployeeRef.current
      ).toLowerCase();
      const match = svc.employees.find(
        (e) => normalizeEmployeeName(e.nameEmployee).toLowerCase() === targetName
      );
      if (match) {
        preferredEmployeeRef.current = undefined;
        await applyEmployeeAndLoadHours(
          appointmentId,
          String(match.idEmployee),
          serviceId
        );
      }
    }
  };

  // Asigna un empleado a la fila y carga sus horarios disponibles. Si hay una
  // hora preferida pendiente (clic en cuadrante), la preselecciona cuando cae
  // dentro de un bloque disponible.
  const applyEmployeeAndLoadHours = async (
    id: string,
    employeeId: string,
    serviceId
  ) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, employee: employeeId, schedule: "" } : item
      )
    );

    if (!employeeId) return;

    setLoadingHoursId(id);
    setLoadingHours(true);

    const hoursResponse = await getAvailableHours_adapter({
      date,
      employeeId,
      priceServiceId: serviceId,
      reserveId: isEdit ? reserveId : null,
    });

    if (hoursResponse.data.length === 0 && hoursResponse.isSuccess) {
      const today = new Date().toISOString().split("T")[0];
      const errorMessage =
        date === today
          ? "El empleado ya no tiene horas disponibles para hoy."
          : "El empleado no tiene horas disponibles para el día seleccionado.";

      setErrorMessage((prev) => ({
        ...prev,
        [id]: errorMessage,
      }));
      setAvailableHours((prev) => ({ ...prev, [id]: [] }));
    } else if (hoursResponse.isSuccess) {
      setErrorMessage((prev) => ({ ...prev, [id]: "" }));
      setAvailableHours((prev) => ({ ...prev, [id]: hoursResponse.data }));

      // Preselección de hora (clic en cuadrante): se consume una sola vez.
      if (preferredHourRef.current) {
        const target = preferredHourRef.current;
        preferredHourRef.current = undefined;
        const matchedHour = pickHourForSlot(hoursResponse.data, target);
        if (matchedHour) {
          setSelectedItems((prevItems) =>
            prevItems.map((item) =>
              item.id === id ? { ...item, schedule: matchedHour } : item
            )
          );
        }
      }
    } else {
      setAvailableHours((prev) => ({ ...prev, [id]: [] }));
      setStateResponse({
        message: "Hubo un error al traer los horarios, intente de nuevo.",
        status: 400,
      });
    }
    setLoadingHours(false);
    setLoadingHoursId(null);
  };

  const handleEmployeeChange = async (id, value) => {
    const serviceId = selectedItems.find((item) => item.id === id)?.service;
    await applyEmployeeAndLoadHours(id, value, serviceId);
  };

  const getEmployeesForService = (serviceId) => {
    const selectedService = services.find(
      (service) => service.idPrice === serviceId
    );
    return selectedService ? selectedService.employees : [];
  };

  useEffect(() => {
    const conflictMessages = {};
    const newConflictingItems = []; // Arreglo para almacenar los IDs en conflicto

    selectedItems.forEach((item) => {
      // "Por asignar" (employee ""): sin estilista no hay colisión de agenda.
      const itemHasEmployee =
        item.employee !== "" && item.employee != null && item.employee !== 0;
      const conflicts = itemHasEmployee
        ? selectedItems.filter(
            (otherItem) =>
              otherItem.employee === item.employee &&
              otherItem.schedule === item.schedule &&
              otherItem.id !== item.id && // Asegúrate de que no se compare consigo mismo
              otherItem.schedule !== ""
          )
        : [];

      if (conflicts.length > 0) {
        const selectedService = services.find(
          (service) => service.idPrice === item.service
        );

        // Validar que el servicio seleccionado existe
        if (selectedService) {
          const employee = selectedService.employees.find(
            (emp) => emp.idEmployee === item.employee
          );

          // Validar que el empleado existe
          if (employee) {
            // Cambiar para que solo se agregue un mensaje único
            if (!conflictMessages[employee.nameEmployee]) {
              conflictMessages[employee.nameEmployee] = `${
                employee.nameEmployee
              } tiene ${
                conflicts.length + 1
              } servicios a la misma hora, por favor corregir.`;
            }
            newConflictingItems.push(item.id); // Agregar el ID al arreglo de conflictos
          }
        }
      }
    });

    // Actualizar el estado de los mensajes de conflicto
    if (Object.keys(conflictMessages).length > 0) {
      setMessageConflicting(Object.values(conflictMessages).join(" "));
      setIdConflictingItem(newConflictingItems); // Solo los IDs en conflicto
    } else {
      setMessageConflicting(""); // No hay conflictos, se limpia el mensaje
      setIdConflictingItem([]);
    }
  }, [selectedItems, services]);

  const handleScheduleChange = (id, value) => {
    setSelectedItems((prevItems) => {
      return prevItems.map((item) =>
        item.id === id ? { ...item, schedule: value } : item
      );
    });
  };

  const handleToggleFija = (id: string) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, isFija: !item.isFija } : item
      )
    );
  };

  const validateOrderHours = () => {
    try {
      const selectedItemsSorted = [...selectedItems].sort((a, b) => {
        const timeA = a.schedule.split(":").map(Number);
        const timeB = b.schedule.split(":").map(Number);
        return timeA[0] - timeB[0] || timeA[1] - timeB[1];
      });
      return (
        JSON.stringify(selectedItems) !== JSON.stringify(selectedItemsSorted)
      );
    } catch (error) {
      return false;
    }
  };

  // useEffect para validar conflictos de horarios
  useValidateScheduleConflicts(selectedItems, services, setEmpleadosOcupados);

  return (
    <div className={`${style.appointmentDetail} ${style.slideIn}`}>
      <div className={style.SelectedCashregisterBody__left__salesOptions}>
        <div className={style.servicesContainer}>
          {isEdit && !isDisabled && !isDisabledNoShow && (
            <span data-testid="reservation-add-service">
              <TertiaryButton
                buttonProps={{
                  onClick: handleAddService,
                  disabled: isDisabled || !customerSelected,
                }}
                icon={AddIcon}
                value="Añadir servicio"
              />
            </span>
          )}
          {!isEdit && !isDisabledNoShow && (
            <div className={style.buttonsContainer}>
              <span data-testid="reservation-add-service">
                <TertiaryButton
                  buttonProps={{
                    onClick: handleAddService,
                    disabled: isDisabled || !customerSelected,
                  }}
                  icon={AddIcon}
                  value="Añadir servicio"
                />
              </span>
              {selectedItems.length > 1 && validateOrderHours() && (
                <button className={style.orderHoursButton} onClick={orderHours}>
                  <IconWrapper icon={HiOutlineBarsArrowDown} />
                  Ordenar horarios
                </button>
              )}
            </div>
          )}
          {getStatusMessage() && (
            <div className={style.comandaMessage}>
              <p>{getStatusMessage()}</p>
              <Icon path={CommandIcon} color="#45348e" size={12} />
            </div>
          )}
          {messageConflicting && (
            <ConflictMessage message={messageConflicting} />
          )}
          {empleadosOcupados.length > 0 && !messageConflicting && (
            <OccupiedEmployeesMessage empleadosOcupados={empleadosOcupados} />
          )}
          {selectedItems?.map((item) => {
            const selectedService = services?.find(
              (service) => service.idPrice === item.service
            );
            const servicePrice = selectedService ? selectedService.price : 0;
            const serviceDuration = item.duration != null
              ? item.duration
              : (selectedService ? selectedService.duration : 0);

            // Verificar si el empleado tiene conflictos
            const hasConflict =
              empleadosOcupados.some(
                (ocupado) => ocupado.idEmpleado === item.id
              ) || idConflictingItem.includes(item.employee); // Verificar si hay conflictos

            // "Por asignar" (sin estilista): no hay disponibilidad de un
            // empleado concreto, así que ofrecemos una grilla genérica de horas
            // (cada 15') para fijar una hora tentativa. La hora real se valida
            // cuando se asigne el estilista.
            const isUnassigned =
              item.employee === "" ||
              item.employee == null ||
              item.employee === 0;

            const genericHours: string[] =
              isUnassigned && item.service
                ? Array.from({ length: 24 * 4 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    return `${String(h).padStart(2, "0")}:${String(m).padStart(
                      2,
                      "0"
                    )}`;
                  })
                : [];

            const baseHours =
              availableHours[item.id] && availableHours[item.id].length > 0
                ? availableHours[item.id]
                : genericHours;

            const hoursSelect = [
              ...(baseHours?.map((hour) => ({
                name: hour,
                value: hour,
              })) || []),
              ...(item.schedule && !baseHours?.includes(item.schedule)
                ? [
                    {
                      name: String(item.schedule),
                      value: String(item.schedule),
                    },
                  ]
                : []),
            ];

            return (
              <div
                key={item.id}
                className={`${style.serviceEmployeePair} ${
                  style.animatedSchedule
                } ${animationClass.includes(item.id) ? style.slideDown : ""}
                ${incompleteItems.includes(item.id) ? style.incomplete : ""}
                ${hasConflict ? style.error : ""}
                ${highlightDetailId && String(item.id) === String(highlightDetailId) ? style.highlightedRow : ""}
                `}
              >
                <span className={style.serviceSearchContainer} data-testid="reservation-service">
                  <SearchSelect
                    textInputProps={{
                      value: item.serviceSearch,
                      handleChange: (e) =>
                        handleSearchServiceChange(item.id, e.target.value),
                      required: false,
                      placeholder: "1. Busca un servicio",
                    }}
                    onSearch={async (searchTerm) => {}}
                    onSelect={(option) => handleServiceChange(item.id, option)}
                    options={getFilteredServiceOptions(
                      item.serviceSearch || ""
                    )}
                    selectedOption={formatSelectedOption(item.service)}
                    disabled={
                      loadingServices ||
                      (isEdit && isDisabled) ||
                      isDisabledNoShow
                    }
                    selectedContainerClassName={style.selectedServiceContainer}
                  />
                </span>
                <>
                  <span className={style.employeeSelectContainer} data-testid="reservation-employee">
                    <SelectInput
                      value={item.employee}
                      handleChange={(e) =>
                        handleEmployeeChange(item.id, e.target.value)
                      }
                      items={[
                        { name: "Por asignar", value: "" },
                        ...[...(getEmployeesForService(item.service) || [])]
                          .sort((a, b) =>
                            (a.nameEmployee || "").localeCompare(
                              b.nameEmployee || ""
                            )
                          )
                          .map((emp) => ({
                            name: emp.nameEmployee,
                            value: String(emp.idEmployee),
                          })),
                      ]}
                      required={false}
                      placeholder={!item.service ? "2. Elige empleado" : "2. Elige empleado"}
                      error={errorMessage[item.id]}
                      touched={errorMessage[item.id] !== ""}
                      disabled={
                        !item.service ||
                        (isEdit && isDisabled) ||
                        isDisabledNoShow ||
                        item.isFija
                      }
                    />
                  </span>
                  {loadingHours && loadingHoursId === item.id ? (
                    <span className={style.inputContainerMessage}>
                      <img
                        src={ValidatingIcon}
                        alt="Validando"
                        className={style.validatingSvg}
                      />
                      <p className={style.inputValidatingMessage}>
                        Buscando horarios...
                      </p>
                    </span>
                  ) : (
                    <span className={style.hourSelectContainer} data-testid="reservation-time">
                      <SelectInput
                        value={item.schedule}
                        handleChange={(e) =>
                          handleScheduleChange(item.id, e.target.value)
                        }
                        items={hoursSelect}
                        required={true}
                        placeholder="3. Hora"
                        touched={true}
                        error={
                          (idConflictingItem.includes(item.employee) &&
                          selectedItems.filter(
                            (i) =>
                              i.employee === item.employee &&
                              i.schedule === item.schedule
                          ).length > 1
                            ? "Modificación necesaria"
                            : "") || (hasConflict ? "Modificación necesaria" : "")
                        }
                        disabled={
                          // "Por asignar": permitir fijar hora si ya hay
                          // servicio elegido; si no hay servicio, deshabilitar.
                          (item.employee === "" && !item.service) ||
                          (isEdit && isDisabled) ||
                          isDisabledNoShow ||
                          item.isFija
                        } // Deshabilitar si hay elementos iniciales o reserva fija
                      />
                    </span>
                  )}
                </>
                <ServicePriceDuration
                  servicePrice={servicePrice}
                  serviceDuration={serviceDuration}
                  onDurationChange={hasModuleAccess(EDIT_DURATION_UUID) ? (val) => {
                    setSelectedItems(
                      selectedItems.map((si) =>
                        si.id === item.id ? { ...si, duration: val } : si
                      )
                    );
                  } : undefined}
                  disabled={isEdit && isDisabled}
                />
                <div className={style.employeeActions}>
                  {hasModuleAccess(EDIT_SCHEDULES_UUID) && (
                    <button
                      className={style.employeeActionBtn}
                      onClick={() => onOpenSchedulesModal?.(item.employee)}
                      disabled={!item.employee}
                      title="Modificar horarios del empleado"
                    >
                      <img src={CalendarIcon} alt="Horarios" />
                    </button>
                  )}
                  {hasModuleAccess(MANAGE_SERVICE_EMPLOYEES_UUID) && (
                    <button
                      className={style.employeeActionBtn}
                      onClick={() => onOpenServicesModal?.(item.employee, item.service)}
                      disabled={!item.service}
                      title="Asignar empleados al servicio"
                    >
                      <img src={EmployeeServiceIcon} alt="Empleados" />
                    </button>
                  )}
                </div>
                {item.employee && !isDisabled && !isDisabledNoShow && (
                  <button
                    className={`${style.fixedLockBtn} ${item.isFija ? style.fixedLockBtnLocked : style.fixedLockBtnUnlocked}`}
                    onClick={() => {
                      if (item.isFija && !hasModuleAccess(UNLOCK_FIXED_RESERVATION_UUID)) return;
                      handleToggleFija(item.id);
                    }}
                    disabled={item.isFija && !hasModuleAccess(UNLOCK_FIXED_RESERVATION_UUID)}
                    title={item.isFija
                      ? (hasModuleAccess(UNLOCK_FIXED_RESERVATION_UUID) ? "Empleado fijo (clic para desbloquear)" : "Empleado fijo — no tienes permiso para desbloquear")
                      : "Fijar empleado"}
                    type="button"
                  >
                    {item.isFija ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
                      </svg>
                    )}
                  </button>
                )}
                {selectedItems?.length > 1 &&
                  !isDisabledNoShow &&
                  !isDisabled && (
                    <button
                      onClick={() => handleRemoveService(item.id)}
                      className={style.removeServiceButton}
                      title="Eliminar servicio"
                    >
                      ✕
                    </button>
                  )}
              </div>
            );
          })}
          {loadingServices && (
            <ValidationMessage message="Buscando servicios..." />
          )}
        </div>
        <Summary selectedItems={selectedItems} services={services} />
      </div>
      <TextAreaInput
        label="Comentarios adicionales"
        placeholder="Añadir comentarios adicionales a la reserva"
        value={additionalComments || ""}
        handleChange={(e) => setAdditionalComments(e)}
        height="20px"
        width="100%"
        maxWidth="100%"
        disabled={isDisabled || isDisabledNoShow}
      />
    </div>
  );
};
